import apiClient from './apiClient';

/**
 * Service for managing the chatbot's memory of user facts
 * This implementation stores facts in MongoDB instead of localStorage
 */

/**
 * Initialize or retrieve chat memory from the API
 * @param {string} userId - The user's ID
 * @returns {Array} - The user's memory facts array
 */
export const getMemory = async (userId) => {
  try {
    const response = await apiClient.get('/api/chat-memory/full');
    if (response.ok) {
      const data = await response.json();
      return data.memory || [];
    }
    console.error('Error retrieving memory:', response.statusText);
    return [];
  } catch (error) {
    console.error('Error retrieving memory:', error);
    return [];
  }
};

/**
 * Add new facts to memory
 * @param {string} userId - The user's ID
 * @param {Array} newFacts - Array of fact strings to remember
 * @returns {Array} - Updated memory facts array
 */
export const addFacts = async (userId, newFacts) => {
  if (!newFacts || !Array.isArray(newFacts) || newFacts.length === 0) {
    // If no facts to add, just get current memory
    const response = await apiClient.get('/api/chat-memory/facts');
    if (response.ok) {
      const data = await response.json();
      return data.facts || [];
    }
    return [];
  }

  try {
    // Filter out empty facts
    const validFacts = newFacts.filter(fact => fact && fact.trim());
    
    if (validFacts.length === 0) {
      return [];
    }

    // Post facts to API
    const response = await apiClient.post('/api/chat-memory/facts', {
      facts: validFacts
    });

    if (response.ok) {
      try {
        const data = await response.json();
        // Check if the response includes facts array directly
        if (data && Array.isArray(data.facts)) {
          return data.facts;
        }
        
        // If not, get the updated facts list
        const getResponse = await apiClient.get('/api/chat-memory/facts');
        if (getResponse.ok) {
          const fetchedData = await getResponse.json();
          return fetchedData.facts || [];
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        // Fallback to get request if we can't parse the response
        const getResponse = await apiClient.get('/api/chat-memory/facts');
        if (getResponse.ok) {
          const fetchedData = await getResponse.json();
          return fetchedData.facts || [];
        }
      }
    }
    
    console.error('Error adding facts:', response.statusText);
    return [];
  } catch (error) {
    console.error('Error adding facts:', error);
    return [];
  }
};

/**
 * Get all facts as an array
 * @param {string} userId - The user's ID
 * @returns {Array} - Array of fact strings
 */
export const getAllFacts = async (userId) => {
  try {
    const response = await apiClient.get('/api/chat-memory/facts');
    if (response.ok) {
      const data = await response.json();
      return data.facts || [];
    }
    console.error('Error getting all facts:', response.statusText);
    return [];
  } catch (error) {
    console.error('Error getting all facts:', error);
    return [];
  }
};

/**
 * Get all facts from memory as a formatted string for the chatbot prompt
 * @param {string} userId - The user's ID
 * @returns {string} - Formatted memory string
 */
export const getFormattedMemory = async (userId) => {
  try {
    const facts = await getAllFacts(userId);
    
    if (!facts || facts.length === 0) {
      return '';
    }
    
    // Format facts for the prompt
    return `NOTABLE FACTS ABOUT THE USER:\n${facts.map(fact => `- ${fact}`).join('\n')}`;
  } catch (error) {
    console.error('Error formatting memory:', error);
    return '';
  }
};

/**
 * Clear all memory for a user
 * @param {string} userId - The user's ID
 */
export const clearMemory = async (userId) => {
  try {
    await apiClient.delete('/api/chat-memory/clear');
    console.log('Memory cleared successfully');
  } catch (error) {
    console.error('Error clearing memory:', error);
  }
};

export default {
  getMemory,
  addFacts,
  getFormattedMemory,
  getAllFacts,
  clearMemory
}; 